import { GridFilterModel } from '@mui/x-data-grid';
import { InfrastructureLevel, InfrastructureType } from '@nohara-dxdev/mt-common';
import { AdvancedFilterModel } from '@nohara-dxdev/react-core';
import { createContext, useContext, ReactNode, useRef } from 'react';

import { useDebounceValue } from '@/hooks';

// Sortable fields matching backend definition
export const itemSortableFields = [
  'name',
  'itemNumber',
  'createdAt',
  'updatedAt',
  'status',
] as const;

export type ItemSortableField = (typeof itemSortableFields)[number];
export type ItemSortOrder = 'asc' | 'desc';

/**
 * ItemFilterValues interface matching the backend API contract
 *
 * Filter fields (constructionTypeId, systemIds, itemTypeIds, floorId, buildingId,
 * name, projectRoleTags) are now handled via the filterModel using RSQL.
 *
 * Special params:
 * - excludeIds: Handled via id=out=(...) RSQL expression (OUT operator)
 * - locationIdOrAdjacentLocationIdEq: Special param for location filtering (handled separately)
 * - infrastructureRelationshipRequirements: Complex infrastructure filtering (handled separately)
 */
export interface ItemFilterValues {
  // Internal filter models (array of DataGrid filter models)
  // Each filterModel in the array is treated as an AND group
  // Multiple filterModels are combined with AND operator
  // This will be converted to RSQL string in the API hook
  // Fields like constructionTypeId, systemIds, itemTypeIds, floorId, buildingId, name, projectRoleTags
  // should be added as filter items in these models
  filterModel?: GridFilterModel[];

  // Advanced filter model (supports AND/OR between conditions)
  // When provided, takes precedence over filterModel
  // Will be converted to GridFilterModel format for RSQL conversion
  advancedFilterModel?: AdvancedFilterModel;

  // Special filter: excludeIds uses OUT operator (id=out=(...))
  // This is handled separately because DataGrid doesn't have a direct OUT operator mapping
  excludeIds?: string[];

  // Special query parameters (handled separately from RSQL)
  locationIdOrAdjacentLocationIdEq?: string;
  infrastructureRelationshipRequirements?: `${InfrastructureType}|${InfrastructureLevel}`[];

  // Full-text search parameter (handled separately from RSQL)
  // Searches both name and itemNumber fields
  nameOrItemNumberMatch?: string;

  // Sorting parameters
  sortField?: ItemSortableField;
  sortOrder?: ItemSortOrder;

  // When true, only return items that have no location assigned.
  // Set by the Room Management screen when no room is selected.
  isUnassignedToLocation?: boolean;

  // When true, only return items that have no subsystem assigned.
  // Set by the Item Management screen when no subsystem is selected.
  isUnassignedToSystem?: boolean;
}

const ItemFilterContext = createContext<ItemFilterValues | undefined>(undefined);

export interface ItemFilterProviderProps {
  children: ReactNode;
  filterValues: ItemFilterValues;
  debounceTime?: number;
}

export const ItemFilterValuesProvider = ({
  children,
  filterValues,
  debounceTime = 500,
}: ItemFilterProviderProps) => {
  const filterValuesRef = useRef<ItemFilterValues | undefined>(filterValues);

  if (JSON.stringify(filterValues) !== JSON.stringify(filterValuesRef.current)) {
    filterValuesRef.current = filterValues;
  }

  const debouncedFilterValues = useDebounceValue(filterValuesRef.current, debounceTime);

  return (
    <ItemFilterContext.Provider value={debouncedFilterValues}>
      {children}
    </ItemFilterContext.Provider>
  );
};

export const useItemFilterValues = (): ItemFilterValues => {
  return useContext(ItemFilterContext) ?? {};
};
